import React, {useContext} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {Link} from "react-router-dom";
import {KeycloakContext} from "../../contextProviders/KeycloakProvider";
import {ModalContext} from "../../contextProviders/ModalProvider";
import useAppActions from "../../../hooks/useAppActions";
import useInitialAppStatus from "../../../hooks/useInitialAppStatus";
import LoadingSpinner from "../../../components/LoadingSpinner";

type AppActionButtonProps = {
  groupId: string;
  isGroupAdmin: boolean;
  appId: string;
  status: "installed" | "available";
  requiresParameters: boolean;
};

/** Action button component that handles install/uninstall logic */
function AppActionButton({
  groupId,
  isGroupAdmin,
  appId,
  status,
  requiresParameters,
}: AppActionButtonProps) {
  const {formatMessage} = useIntl();
  const modalContext = useContext(ModalContext);
  const {isSchAdmin} = useContext(KeycloakContext);

  const isAdmin = isSchAdmin || isGroupAdmin;

  // Only fetch app actions if user is admin
  const appActions = isAdmin
    ? useAppActions(groupId)
    : {
        loadingStates: {} as Record<string, boolean>,
        installApp: () => undefined,
        cancelInstall: () => undefined,
        uninstallApp: () => undefined,
      };

  const initialAppStatus = isAdmin
    ? useInitialAppStatus({
        groupId,
        apps: [{appId, isInstalled: status === "installed"}],
      })
    : {loadingStates: {}};

  const {loadingStates, installApp, cancelInstall, uninstallApp} = appActions;
  const {loadingStates: initialLoadingStates} = initialAppStatus;

  if (!isAdmin) {
    return null;
  }

  const isLoading = loadingStates[appId] || initialLoadingStates[appId];
  const isDisabled = initialLoadingStates[appId] === undefined;

  const handleUninstallClick = () => {
    modalContext.showModal({
      headline: formatMessage({id: "appDetails.modal.uninstall.headline"}),
      body: formatMessage({id: "appDetails.modal.uninstall.body"}),
      primaryButton: {
        name: formatMessage({id: "general.button.label.uninstall"}),
        onClick: () => uninstallApp(appId),
      },
      secondaryButton: {
        name: formatMessage({id: "general.button.label.cancel"}),
        onClick: modalContext.hideModal,
      },
    });
  };

  const handleInstallClick = () => {
    installApp(appId);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="infoTile__actions">
        <div className="infoTile__actions__loading">
          <FormattedMessage
            id={
              status === "installed"
                ? "infoTile.status.uninstall"
                : "infoTile.status.install"
            }
          />
          <LoadingSpinner />
        </div>
        {status === "available" && (
          <button onClick={() => cancelInstall(appId)}>
            <FormattedMessage id="general.button.label.cancel" />
          </button>
        )}
      </div>
    );
  }

  // Installed app - show uninstall button
  if (status === "installed") {
    return (
      <button
        id="uninstall_button"
        className="linkButton"
        onClick={handleUninstallClick}
        disabled={isDisabled}
      >
        <FormattedMessage id="general.button.label.uninstall" />
      </button>
    );
  }

  // Available app - show install button
  if (status === "available") {
    // If app requires parameters, link to config page
    if (requiresParameters) {
      return (
        <Link
          className="linkButton"
          to={`/groups/${groupId}/apps/available/${appId}/parameters`}
          state={{from: "app"}}
        >
          <FormattedMessage id="general.button.label.install" />
        </Link>
      );
    }

    // Install button
    return (
      <button
        className="linkButton"
        onClick={handleInstallClick}
        disabled={isDisabled}
      >
        <FormattedMessage id="general.button.label.install" />
      </button>
    );
  }

  return null;
}

export default AppActionButton;
